/*
 * BulkUploadApprovalList.tsx
 * This approval list Component is used for representing all the details of errors logged during the file upload process.
 */

import { TBulkUploadResponse } from "../../../types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faList,
  faExclamationCircle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";
import useToast from "../../../hooks/useToast";
import { DownloadCustomerErrorReport } from "../../../utils/api.constant";
import AuthUtil from "../../../utils/auth";
import { eHTTPStatusCode, eResultCode } from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  DownloadOutlined,
} from "@ant-design/icons";
import { UNAUTHORIZEDREQUEST } from "../../../utils/constants";
import { Button } from "antd";

type TProps = {
  bulkUploadResponse?: TBulkUploadResponse;
  backToUpload: () => void;
};

export default function BulkUploadApprovalList(props: TProps) {
  const { bulkUploadResponse } = props;
  const [isLoading, setIsLoading] = useState(false);
  const { onShowToast } = useToast();

  if (!bulkUploadResponse) {
    return null;
  }

  const stats = [
    {
      label: "Total Records",
      value: bulkUploadResponse.all,
      color: "var(--bs-info)",
      icon: faList,
    },
    {
      label: "Errors",
      value: bulkUploadResponse.error,
      color: "var(--bs-danger)",
      icon: faExclamationCircle,
    },
    {
      label: "Unfailing",
      value: bulkUploadResponse.unfailing,
      color: "var(--bs-success)",
      icon: faCheckCircle,
    },
    // {
    //   label: "Replaced",
    //   value: bulkUploadResponse.replaced,
    //   color: "var(--bs-warning)",
    //   icon: faSyncAlt,
    // },
  ];

  const handleDownload = async () => {
    try {
      // Start loading
      setIsLoading(true);

      // Fetch the XLSX file
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${DownloadCustomerErrorReport}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Ensure JSON payload
            ...AuthUtil.getToken(),
          },
          body: JSON.stringify({
            data: {
              CustomerBulkUploadTempId: props?.bulkUploadResponse?.id,
            },
          }), // Convert body to JSON string
        }
      );

      // Check if the response is OK
      if (response.status === eHTTPStatusCode.OK) {
        // Extract content-disposition header to get the file name
        const contentDisposition = response.headers.get("Content-Disposition");
        // Try to infer extension from response Content-Type header
        const contentType: any = response.headers.get("Content-Type");
        console.log("contentType", contentType);
        let extensionMap: any = {};
        if (contentType) {
          extensionMap = {
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
              "xlsx",
            "application/pdf": "pdf",
            "text/csv": "csv",
            "application/zip": "zip",
          };
        }
        const ext = extensionMap[contentType];
        let fileName = ext
          ? `${props?.bulkUploadResponse?.customerName}_warranty_file_validated_report` +
            `.${ext}`
          : `${props?.bulkUploadResponse?.customerName}_warranty_file_validated_report.xlsx`; // Default file name
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch?.[1]) {
            fileName = fileNameMatch[1];
          }
        }

        // Convert response to Blob
        const blob = await response.blob();

        // Create a URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a link element and set the URL as href
        const link = document.createElement("a");
        link.href = url;

        // Set the file name for the download
        link.download = fileName; // Use file name from response header or default

        // Append the link to the document and trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up by removing the link and revoking the URL
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: "File downloaded successfully!",
        });
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: UNAUTHORIZEDREQUEST,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content:
            "Error while downloading sample file, please try again later.",
        });
      }
    } catch (error) {
      // Handle any exceptions
      console.error("Download error:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Error downloading file",
      });
    } finally {
      // Stop loading
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        padding: "0.4rem",
      }}
    >
      <h3>File Upload Summary</h3>
      {props?.bulkUploadResponse?.errorCode !== eResultCode.NOT_FOUND ? (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {stats.map((stat, index) => (
            <li
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0.75rem 0",
                borderBottom:
                  index < stats.length - 1 ? "1px solid #dee2e6" : "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <FontAwesomeIcon
                  icon={stat.icon}
                  style={{
                    color: stat.color,
                    fontSize: "1.5rem",
                    marginRight: "1rem",
                    width: "20px",
                  }}
                />
                <span>{stat.label}</span>
              </div>
              <span style={{ fontSize: "1.25rem", fontWeight: "bold" }}>
                {stat.value}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div
        style={{
          display: "flex",
          marginTop: "0.5rem",
          textAlign: "right",
          gap: "0.5rem",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="default"
          disabled={isLoading}
          loading={isLoading}
          size="small"
          onClick={props?.backToUpload}
        >
          <ArrowLeftOutlined />
          Back to Upload
        </Button>
        {props?.bulkUploadResponse?.errorCode !== eResultCode.NOT_FOUND ? (
          <Button
            disabled={isLoading}
            loading={isLoading}
            size="small"
            type="primary"
            onClick={handleDownload}
          >
            <DownloadOutlined />
            Warranty Error Report
          </Button>
        ) : null}
      </div>
      {bulkUploadResponse.errorMessage && (
        <div
          style={{
            marginTop: "1rem",
            padding: "1rem",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            border: "1px solid #f5c6cb",
            borderRadius: "0.25rem",
          }}
        >
          {(() => {
            try {
              const error = JSON.parse(bulkUploadResponse.errorMessage);
              return (
                <div>
                  <p>{error.message}</p>
                  {error.reasons && Array.isArray(error.reasons) && (
                    <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
                      {error.reasons.map((reason: string, i: number) => (
                        <li key={i}>{reason}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            } catch (e) {
              return <span>{bulkUploadResponse.errorMessage}</span>;
            }
          })()}
        </div>
      )}
    </div>
  );
}
